import {
  filterAgenticTab,
  isAgenticFeatureEnabled,
  isGatedTabId,
  loadAgenticFeatureFlag,
  normalizeTabId,
} from "@shared/ui/agenticFeatures";
import {
  filterOwnLedgerTabs,
  isOwnLedgerEnabled,
  loadOwnLedgerFeatures,
} from "@shared/ui/ownLedgerFeatures";
import {
  orderedVisibleTabIds,
  WEB_TAB_PREF_DEFINITIONS,
  withAstroTabIcon,
  type TabPreferences,
} from "@shared/ui/tabPreferences";

export type WebTabKind = "native" | "injected";

export type WebTabId =
  | "puts"
  | "puts-plus"
  | "calls"
  | "calls-plus"
  | "options"
  | "stocks"
  | "history-local"
  | "settings"
  | "analytics-local"
  | "wheel-lab"
  | "lab"
  | "tax"
  | "market"
  | "astrology"
  | "pfolio"
  | "import"
  | "agents";

export interface WebTabDef {
  id: WebTabId;
  label: string;
  kind: WebTabKind;
  /** Extension button id when injected (matches src/ui/tabs.ts INJECTED_TAB_IDS). */
  injectedId?: string;
  /** Stock Forge native data-wheel-tab value when applicable. */
  wheelTab?: string;
}

/** Full wheel tab bar — native Stock Forge tabs, then extension injected tabs (Settings is a toolbar icon). */
export const WEB_TAB_BAR: WebTabDef[] = [
  { id: "puts", label: "Puts", kind: "native", wheelTab: "puts" },
  { id: "puts-plus", label: "Puts+", kind: "native", wheelTab: "puts-plus" },
  { id: "calls", label: "Calls", kind: "native", wheelTab: "calls" },
  { id: "calls-plus", label: "Calls+", kind: "native", wheelTab: "calls-plus" },
  { id: "options", label: "Options", kind: "native", wheelTab: "options" },
  { id: "stocks", label: "Stocks", kind: "native", wheelTab: "stocks" },
  { id: "history-local", label: "History", kind: "native", wheelTab: "history-local" },
  { id: "analytics-local", label: "Analytics", kind: "injected", injectedId: "sfe-analytics-local-tab" },
  { id: "wheel-lab", label: "Wheel Lab", kind: "injected", injectedId: "sfe-wheel-lab-tab" },
  { id: "lab", label: "Lab", kind: "injected", injectedId: "sfe-lab-tab" },
  { id: "pfolio", label: "Pfolio", kind: "injected", injectedId: "sfe-pfolio-tab" },
  { id: "market", label: "Market", kind: "injected", injectedId: "sfe-market-tab" },
  { id: "astrology", label: "Astro", kind: "injected", injectedId: "sfe-astrology-tab" },
  { id: "tax", label: "Tax", kind: "injected", injectedId: "sfe-tax-tab" },
  { id: "import", label: "Import", kind: "injected", injectedId: "sfe-import-tab" },
  { id: "agents", label: "Agentic", kind: "injected", injectedId: "sfe-agents-tab" },
  { id: "settings", label: "Settings", kind: "native", wheelTab: "settings" },
];

export const INJECTED_WEB_TAB_IDS = new Set<WebTabId>(
  WEB_TAB_BAR.filter((tab) => tab.kind === "injected").map((tab) => tab.id),
);

/**
 * Tabs whose primary dataset still comes from Stock Forge APIs
 * (vs own-ledger / Robinhood import / paper Agentic).
 */
export const STOCKFORGE_DATA_TAB_IDS = new Set<WebTabId>([
  "puts",
  "calls",
]);

export function tabUsesStockForgeData(tabId: WebTabId): boolean {
  return STOCKFORGE_DATA_TAB_IDS.has(tabId);
}

export function tabButtonClasses(tab: WebTabDef, active: boolean): string {
  const sf = tabUsesStockForgeData(tab.id) ? " sfe-web-tab-sf-source" : "";
  if (tab.kind === "injected") {
    const selected = active ? " sfe-tab-selected" : "";
    return `small ghost sfe-web-tab${selected}${sf}`;
  }
  return `small sfe-web-tab ${active ? "active" : "ghost"}${sf}`;
}

export function tabButtonAttrs(tab: WebTabDef): string {
  const attrs = [`type="button"`, `data-tab="${tab.id}"`, `role="tab"`];
  if (tab.wheelTab) attrs.push(`data-wheel-tab="${tab.wheelTab}"`);
  if (tab.kind === "injected") {
    attrs.push('data-sfe-injected="true"');
  }
  if (tab.injectedId) attrs.push(`id="${tab.injectedId}"`);
  if (tabUsesStockForgeData(tab.id)) {
    attrs.push('data-sf-source="true"');
    attrs.push('title="Main data from Stock Forge"');
  }
  return attrs.join(" ");
}

/** Label + optional Stock Forge source marker (orange corner dot). */
export function tabButtonInnerHtml(tab: WebTabDef, labelHtml: string): string {
  const labeled = withAstroTabIcon(tab.id, labelHtml);
  if (!tabUsesStockForgeData(tab.id)) return labeled;
  return `${labeled}<span class="sfe-web-tab-sf-dot" aria-hidden="true"></span>`;
}

export function bodyAttrForTab(tabId: WebTabId): string | null {
  switch (tabId) {
    case "analytics-local":
      return "data-sfe-analytics-plus-active";
    case "wheel-lab":
      return "data-sfe-wheel-lab-active";
    case "lab":
      return "data-sfe-lab-active";
    case "tax":
      return "data-sfe-tax-active";
    case "pfolio":
      return "data-sfe-pfolio-active";
    case "import":
      return "data-sfe-import-active";
    case "agents":
      return "data-sfe-agents-active";
    default:
      return null;
  }
}

export function findWebTabDef(id: WebTabId | "settings"): WebTabDef | undefined {
  return WEB_TAB_BAR.find((tab) => tab.id === id);
}

export function visibleWebTabs(prefs: TabPreferences): WebTabDef[] {
  const defs = filterOwnLedgerTabs(filterAgenticTab(WEB_TAB_PREF_DEFINITIONS));
  const ids = orderedVisibleTabIds(defs, prefs);
  const byId = new Map(WEB_TAB_BAR.map((tab) => [tab.id, tab]));
  return ids
    .map((id) => byId.get(id as WebTabId))
    .filter((tab): tab is WebTabDef => tab != null && tab.id !== "settings");
}

export function visibleWebContentTabIds(): WebTabId[] {
  return WEB_TAB_BAR.map((tab) => tab.id).filter((id) => {
    if (id === "settings") return false;
    if (isGatedTabId(id) && !isAgenticFeatureEnabled()) return false;
    if (
      (id === "history-local" ||
        id === "analytics-local" ||
        id === "wheel-lab" ||
        id === "lab" ||
        id === "pfolio") &&
      !isOwnLedgerEnabled()
    ) {
      return false;
    }
    return true;
  });
}

export { loadAgenticFeatureFlag, loadOwnLedgerFeatures, normalizeTabId };
